// src/components/UserMeet/User-Meet-Private/BreakoutCountdownTimer.jsx
import React, { useState, useEffect } from "react";
import { Clock, AlertCircle } from "lucide-react";

export default function BreakoutCountdownTimer({ expiresAt, onExpire }) {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [percentage, setPercentage] = useState(100);

  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const remaining = expiry - now;

      if (remaining <= 0) {
        setTimeRemaining(0);
        setPercentage(0);
        onExpire?.();
        return;
      }

      const totalMs = expiry - new Date(expiry - 60 * 60 * 1000);
      const pct = Math.max(0, Math.min(100, (remaining / totalMs) * 100));

      setTimeRemaining(remaining);
      setPercentage(pct);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, onExpire]);

  if (timeRemaining === null) return null;

  const totalSeconds = Math.floor(timeRemaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const isWarning = minutes < 5;
  const isCritical = minutes < 2;

  return (
    <div className={`relative flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-semibold overflow-hidden ${
      isCritical 
        ? 'bg-red-500/10 border-red-500/30 animate-pulse' 
        : isWarning 
        ? 'bg-yellow-500/10 border-yellow-500/30' 
        : 'bg-white/5 border-white/15'
    }`}>
      {/* Icon */}
      <div className={`flex-shrink-0 ${
        isCritical ? 'text-red-400' : isWarning ? 'text-yellow-400' : 'text-green-400'
      }`}>
        {isCritical ? <AlertCircle size={16} /> : <Clock size={16} />}
      </div>
      
      {/* Time */}
      <div className="text-white font-mono text-base tracking-wide">
        {minutes}:{seconds.toString().padStart(2, "0")}
      </div>
      
      {/* Label */}
      <div className="text-xs text-white/50">remaining</div>
      
      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
        <div 
          className={`h-full transition-all duration-1000 linear ${
            isCritical 
              ? 'bg-gradient-to-r from-red-500 to-red-600' 
              : isWarning 
              ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' 
              : 'bg-gradient-to-r from-green-500 to-emerald-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}